import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const PlatformIcon = ({ platform, className = "text-2xl" }) => {
  switch(platform) {
    case 'facebook':
      return <i className={`fab fa-facebook text-blue-600 ${className}`}></i>;
    case 'google':
      return <i className={`fab fa-google text-red-500 ${className}`}></i>;
    case 'tiktok':
      return <i className={`fab fa-tiktok text-black ${className}`}></i>;
    case 'rekindle':
      return <span className={className}>🔥</span>;
    default:
      return <span className={className}>📊</span>;
  }
};

const Reports = () => {
  const [searchParams] = useSearchParams();
  const subaccountId = searchParams.get('subaccount_id');
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('7days');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [adSpendWarning, setAdSpendWarning] = useState(null);
  const [pricingSettings, setPricingSettings] = useState({
    trial_price: 0.0,
    enrollment_price: 149.0
  });

  // Modal state for platform contacts
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformContacts, setPlatformContacts] = useState([]);

  // Function to check if yesterday's ad spend was entered
  const checkAdSpendWarning = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      // Get the latest ad spend record
      const response = await fetch(`${backendUrl}/api/ad-spend/latest/${subaccountId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.latest_date) {
          const latestDate = new Date(data.latest_date);
          const yesterdayDate = new Date(yesterday);
          
          if (latestDate < yesterdayDate) {
            setAdSpendWarning({
              show: true,
              lastUpdateDate: latestDate.toLocaleDateString()
            });
          } else {
            setAdSpendWarning(null);
          }
        } else {
          // No ad spend records found
          setAdSpendWarning({
            show: true,
            lastUpdateDate: 'Never'
          });
        }
      }
    } catch (err) {
      console.log('Could not check ad spend warning:', err);
    }
  };
  const fetchPricingSettings = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/pricing-settings/${subaccountId}`);
      if (response.ok) {
        const data = await response.json();
        setPricingSettings(data);
      }
    } catch (err) {
      console.log('Using default pricing settings');
    }
  };

  useEffect(() => {
    if (subaccountId) {
      fetchPricingSettings();
      generateReport();
      checkAdSpendWarning();
    }
  }, [subaccountId, dateRange]);

  const handleTimeframeChange = (timeframe) => {
    setSelectedTimeframe(timeframe);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    let startDate;
    let endDate;

    switch (timeframe) {
      case '7days':
        // 7 days ago to yesterday (excluding today)
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = yesterday;
        break;
      case '30days':
        // 30 days ago to yesterday (excluding today)
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = yesterday;
        break;
      case 'life':
        // Far back date to today (including today)
        startDate = new Date('2020-01-01');
        endDate = today;
        break;
      default:
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = yesterday;
    }

    const newDateRange = {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
    
    setDateRange(newDateRange);
    
    // Auto-refresh the report when date range changes
    setTimeout(() => {
      generateReportWithDateRange(newDateRange);
    }, 100);
  };

  const generateReportWithDateRange = async (dateRange = null) => {
    const reportDateRange = dateRange || dateRange;
    
    try {
      setLoading(true);
      setError(null);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const requestBody = {
        location_id: subaccountId,
        start_date: reportDateRange.start_date,
        end_date: reportDateRange.end_date
      };

      const response = await fetch(`${backendUrl}/api/reports/comprehensive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.status === 401) {
        const errorData = await response.json();
        setError(`Authentication Error: ${errorData.detail || 'OAuth token has expired. Please reconnect to GoHighLevel from the home page.'}`);
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to generate report: ${response.statusText}`);
      }
      
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    return generateReportWithDateRange(dateRange);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatCPL = (value, platform = null) => {
    // Show dash for Rekindle when there's no ad spend
    if (platform === 'rekindle') {
      return '-';
    }
    return formatCurrency(value);
  };

  const formatPercentage = (value, platform = null) => {
    // Show infinity for Rekindle when there's no ad spend  
    if (platform === 'rekindle') {
      return '∞';
    }
    return `${Math.round(value)}%`;
  };

  const formatROI = (value, platform = null) => {
    // Show infinity for Rekindle when there's no ad spend
    if (platform === 'rekindle') {
      return '∞';
    }
    return `${value.toFixed(1)}X`;
  };

  // Function to fetch contacts for a specific platform
  const fetchPlatformContacts = async (platform) => {
    try {
      setModalLoading(true);
      setModalError(null);
      setSelectedPlatform(platform);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const requestBody = {
        location_id: subaccountId,
        platform: platform,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      };

      const response = await fetch(`${backendUrl}/api/reports/platform-contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.status === 401) {
        const errorData = await response.json();
        setModalError(`Authentication Error: ${errorData.detail || 'OAuth token has expired. Please reconnect to GoHighLevel.'}`);
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to fetch contacts: ${response.statusText}`);
      }
      
      const data = await response.json();
      setPlatformContacts(data.contacts || []);
      setShowContactsModal(true);
      
    } catch (err) {
      setModalError(err.message);
    } finally {
      setModalLoading(false);
    }
  };

  // Function to close the modal
  const closeContactsModal = () => {
    setShowContactsModal(false);
    setSelectedPlatform(null);
    setPlatformContacts([]);
    setModalError(null);
  };

  if (!subaccountId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Missing Location ID</h2>
            <p className="text-gray-600">Please access reports from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Generating Report</h2>
          <p className="text-gray-600">Calculating marketing attribution data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Report Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button 
                onClick={generateReport}
                className="w-full text-white px-6 py-2 rounded-lg hover:opacity-90 transition-colors"
                style={{backgroundColor: '#52aef2'}}
              >
                Try Again
              </button>
              {error.includes('OAuth') || error.includes('Authentication') ? (
                <a 
                  href="/"
                  className="block w-full text-white px-6 py-2 rounded-lg hover:opacity-90 transition-colors text-center"
                  style={{backgroundColor: '#d4161d'}}
                >
                  🔗 Reconnect to GoHighLevel
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ margin: '25px' }}>
      {/* Date Range Filter */}
      <div className="bg-white border-b border-gray-200 rounded-t-lg">
        <div className="px-6 py-4">
          <div className="flex justify-center">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
              {['7days', '30days', 'life'].map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => handleTimeframeChange(timeframe)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    selectedTimeframe === timeframe 
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {timeframe === '7days' && '7 Days'}
                  {timeframe === '30days' && '30 Days'}
                  {timeframe === 'life' && 'Life'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="py-6">
        
        {/* Report Period */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">10X Game Plan</h2>
            <p className="text-gray-600">
              Report Period: {new Date(dateRange.start_date).toLocaleDateString()} - {new Date(dateRange.end_date).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Ad Spend Warning */}
        {adSpendWarning && adSpendWarning.show && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <i className="fas fa-exclamation-triangle text-yellow-400 text-xl"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Update your ad spend!</strong> Your last update was on{' '}
                  <span className="font-medium">{adSpendWarning.lastUpdateDate}</span>
                </p>
              </div>
              <div className="ml-auto pl-3">
                <a 
                  href={`/ad-spend?subaccount_id=${subaccountId}`}
                  className="text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors text-sm"
                  style={{backgroundColor: '#f9d548', color: '#333'}}
                >
                  Update Ad Spend
                </a>
              </div>
            </div>
          </div>
        )}

        {reportData && (
          <>
            {/* Summary Cards - 6 KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
              
              {/* 1. Total Ad Spend */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xl font-bold" style={{color: '#b27d2e'}}>{formatCurrency(reportData.totals.ad_spend)}</p>
                    <p className="text-gray-600 text-xs">Total Ad Spend</p>
                  </div>
                  <div className="text-2xl" style={{color: '#b27d2e'}}>
                    <i className="fas fa-dollar-sign"></i>
                  </div>
                </div>
                <div className="text-gray-500 text-xs">
                  {(() => {
                    const days = Math.max(1, Math.ceil((new Date(dateRange.end_date) - new Date(dateRange.start_date)) / (1000 * 60 * 60 * 24)) + 1);
                    const avgDaily = reportData.totals.ad_spend / days;
                    return `${formatCurrency(avgDaily)} Avg. Daily`;
                  })()}
                </div>
              </div>

              {/* 2. Leads */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xl font-bold" style={{color: '#52aef2'}}>{reportData.totals.leads}</p>
                    <p className="text-gray-600 text-xs">Leads</p>
                  </div>
                  <div className="text-2xl" style={{color: '#52aef2'}}>
                    <i className="fas fa-users"></i>
                  </div>
                </div>
                <div className="text-gray-500 text-xs">
                  {reportData.totals.leads > 0 && reportData.totals.ad_spend > 0 
                    ? `${formatCurrency(reportData.totals.ad_spend / reportData.totals.leads)} CPL`
                    : '$0.00 CPL'
                  }
                </div>
              </div>

              {/* 3. Appointments */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xl font-bold" style={{color: '#52aef2'}}>{reportData.totals.appointments}</p>
                    <p className="text-gray-600 text-xs">Appointments</p>
                  </div>
                  <div className="text-2xl" style={{color: '#52aef2'}}>
                    <i className="fas fa-calendar-check"></i>
                  </div>
                </div>
                <div className="text-gray-500 text-xs">
                  {reportData.totals.leads > 0 
                    ? `${Math.round((reportData.totals.appointments / reportData.totals.leads) * 100)}% Booking`
                    : '0% Booking'
                  }
                </div>
              </div>

              {/* 4. Shows */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xl font-bold" style={{color: '#f9d548'}}>{reportData.totals.shows}</p>
                    <p className="text-gray-600 text-xs">Shows</p>
                  </div>
                  <div className="text-2xl" style={{color: '#f9d548'}}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                </div>
                <div className="text-gray-500 text-xs">
                  {reportData.totals.appointments > 0 
                    ? `${Math.round((reportData.totals.shows / reportData.totals.appointments) * 100)}% Show Rate`
                    : '0% Show Rate'
                  }
                </div>
              </div>

              {/* 5. Trials */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xl font-bold" style={{color: '#d4161d'}}>{reportData.totals.trials}</p>
                    <p className="text-gray-600 text-xs">Trials</p>
                  </div>
                  <div className="text-2xl" style={{color: '#d4161d'}}>
                    <i className="fas fa-user-clock"></i>
                  </div>
                </div>
                <div className="text-gray-500 text-xs">
                  {(() => {
                    // Calculate trial revenue using pricing settings
                    const trialRevenue = reportData.totals.trials * pricingSettings.trial_price;
                    return `${formatCurrency(trialRevenue)} Cash`;
                  })()}
                </div>
              </div>

              {/* 6. Enrollments */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xl font-bold" style={{color: '#d4161d'}}>{reportData.totals.enrollments}</p>
                    <p className="text-gray-600 text-xs">Enrollments</p>
                  </div>
                  <div className="text-2xl" style={{color: '#d4161d'}}>
                    <i className="fas fa-trophy"></i>
                  </div>
                </div>
                <div className="text-gray-500 text-xs">
                  {(() => {
                    // Calculate enrollment revenue using pricing settings
                    const enrollmentRevenue = reportData.totals.enrollments * pricingSettings.enrollment_price;
                    return `${formatCurrency(enrollmentRevenue)} Projected`;
                  })()}
                </div>
              </div>

            </div>

            {/* Main Report Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4" style={{background: 'linear-gradient(to right, #d4161d, #b27d2e)'}}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">📊 Marketing Attribution Breakdown</h3>
                  <p className="text-blue-100 text-sm">💡 Click on any platform row to view contacts</p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">CPL</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Booking Ratio</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Show Up Ratio</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Trial Ratio</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollment Ratio</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Collected</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Projected MLV</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Flywheel</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">ROI</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Platform Rows - Show all platforms, but always include Rekindle even with no ad spend */}
                    {reportData.platforms.filter(platform => 
                      platform.platform === 'rekindle' || // Always show Rekindle 
                      platform.leads > 0 || platform.appointments > 0 || platform.ad_spend > 0 || platform.trials > 0 || platform.enrollments > 0
                    ).map((platform, index) => (
                      <tr 
                        key={platform.platform} 
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors`}
                        onClick={() => fetchPlatformContacts(platform.platform)}
                        title={`Click to view ${platform.platform} contacts`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <PlatformIcon platform={platform.platform} className="text-lg" />
                              <span className="text-sm font-medium text-gray-900 capitalize">{platform.platform}</span>
                            </div>
                            <div className="text-gray-400 opacity-50">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {formatCPL(platform.cpl, platform.platform)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${platform.appointment_ratio >= 50 ? 'text-green-600' : platform.appointment_ratio >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatPercentage(platform.appointment_ratio)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${platform.show_ratio >= 70 ? 'text-green-600' : platform.show_ratio >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatPercentage(platform.show_ratio)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${platform.trial_ratio >= 30 ? 'text-green-600' : platform.trial_ratio >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatPercentage(platform.trial_ratio)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${platform.enrollment_ratio >= 50 ? 'text-green-600' : platform.enrollment_ratio >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatPercentage(platform.enrollment_ratio)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {formatCurrency(platform.cash_collected)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {formatCurrency(platform.projected_mlv)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${platform.flywheel >= 100 ? 'text-green-600' : platform.flywheel >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatPercentage(platform.flywheel, platform.platform)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${platform.roi >= 5 ? 'text-green-600' : platform.roi >= 2 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatROI(platform.roi, platform.platform)}
                          </span>
                        </td>
                      </tr>
                    ))}

                    {/* Totals Row */}
                    <tr className="bg-gradient-to-r from-gray-100 to-gray-200 font-bold">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-bold text-gray-900">TOTALS</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {formatCurrency(reportData.totals.cpl)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${reportData.totals.appointment_ratio >= 50 ? 'text-green-600' : reportData.totals.appointment_ratio >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {formatPercentage(reportData.totals.appointment_ratio)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${reportData.totals.show_ratio >= 70 ? 'text-green-600' : reportData.totals.show_ratio >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {formatPercentage(reportData.totals.show_ratio)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${reportData.totals.trial_ratio >= 30 ? 'text-green-600' : reportData.totals.trial_ratio >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {formatPercentage(reportData.totals.trial_ratio)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${reportData.totals.enrollment_ratio >= 50 ? 'text-green-600' : reportData.totals.enrollment_ratio >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {formatPercentage(reportData.totals.enrollment_ratio)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {formatCurrency(reportData.totals.cash_collected)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {formatCurrency(reportData.totals.projected_mlv)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${reportData.totals.flywheel >= 100 ? 'text-green-600' : reportData.totals.flywheel >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {formatPercentage(reportData.totals.flywheel)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${reportData.totals.roi >= 5 ? 'text-green-600' : reportData.totals.roi >= 2 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {formatROI(reportData.totals.roi)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Contacts Modal */}
            {showContactsModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                  {/* Modal Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{background: 'linear-gradient(to right, #d4161d, #52aef2)'}}>
                    <div className="flex items-center space-x-3">
                      <PlatformIcon platform={selectedPlatform} className="text-2xl text-white" />
                      <div>
                        <h3 className="text-xl font-bold text-white capitalize">{selectedPlatform} Contacts</h3>
                        <p className="text-blue-100 text-sm">
                          {new Date(dateRange.start_date).toLocaleDateString()} - {new Date(dateRange.end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closeContactsModal}
                      className="text-white hover:text-gray-200 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {modalLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading contacts...</p>
                      </div>
                    ) : modalError ? (
                      <div className="text-center py-8">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Contacts</h4>
                        <p className="text-gray-600 mb-4">{modalError}</p>
                        <button 
                          onClick={() => fetchPlatformContacts(selectedPlatform)}
                          className="text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors"
                          style={{backgroundColor: '#52aef2'}}
                        >
                          Try Again
                        </button>
                      </div>
                    ) : platformContacts.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 text-4xl mb-4">👥</div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">No Contacts Found</h4>
                        <p className="text-gray-600">No contacts found for {selectedPlatform} in the selected date range.</p>
                      </div>
                    ) : (
                      <>
                        {/* Contact Count */}
                        <div className="mb-6 text-center">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {platformContacts.length} Contact{platformContacts.length !== 1 ? 's' : ''} Found
                          </h4>
                        </div>

                        {/* Contacts Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {platformContacts.map((contact, index) => (
                            <div 
                              key={contact.id || index} 
                              className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all duration-200 transform hover:scale-[1.02]"
                              onClick={() => window.open(`https://app.thefollowup.ninja/v2/location/${subaccountId}/contacts/detail/${contact.id}`, '_blank')}
                              title="Click to view contact in Follow-up Ninja"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h5 className="font-semibold text-gray-900">
                                    {contact.firstName || contact.lastName 
                                      ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
                                      : 'Unknown Name'
                                    }
                                  </h5>
                                  <p className="text-sm text-gray-600">
                                    Added: {contact.dateAdded ? new Date(contact.dateAdded).toLocaleDateString() : 'Unknown'}
                                  </p>
                                  {/* Pipeline Stage */}
                                  {contact.pipelineStage && contact.pipelineStage !== 'No Stage' && (
                                    <p className="text-xs text-purple-600 font-medium mt-1">
                                      Stage: {contact.pipelineStage}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end space-y-1">
                                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                    contact.attribution === 'facebook' ? 'bg-blue-100 text-blue-800' :
                                    contact.attribution === 'google' ? 'bg-red-100 text-red-800' :
                                    contact.attribution === 'tiktok' ? 'bg-gray-100 text-gray-800' :
                                    contact.attribution === 'rekindle' ? 'bg-orange-100 text-orange-800' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {contact.attribution}
                                  </span>
                                  <div className="text-gray-400 text-xs">
                                    Click to view →
                                  </div>
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                {contact.email && (
                                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <span className="truncate">{contact.email}</span>
                                  </div>
                                )}
                                
                                {contact.phone && (
                                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span>{contact.phone}</span>
                                  </div>
                                )}
                                
                                {(contact.source || contact.contactSource || contact.utmSource) && (
                                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                    <span className="truncate">
                                      {contact.utmSource || contact.contactSource || contact.source}
                                    </span>
                                  </div>
                                )}
                                
                                {contact.tags && contact.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {contact.tags.slice(0, 3).map((tag, tagIndex) => (
                                      <span key={tagIndex} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                        {tag}
                                      </span>
                                    ))}
                                    {contact.tags.length > 3 && (
                                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                        +{contact.tags.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                    <button
                      onClick={closeContactsModal}
                      className="text-white px-6 py-2 rounded-lg hover:opacity-90 transition-colors"
                      style={{backgroundColor: '#d4161d'}}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 text-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a 
                  href={`/ad-spend?subaccount_id=${subaccountId}`}
                  className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors inline-block"
                  style={{backgroundColor: '#52aef2'}}
                >
                  💰 Enter Ad Spend
                </a>
                <a 
                  href={`/settings?subaccount_id=${subaccountId}`}
                  className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors inline-block"
                  style={{backgroundColor: '#d4161d'}}
                >
                  🧮 Calculate KPIs
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;